import React, { useEffect, useState } from "react";
import { ReportAPI } from "../../api/apiClient";

interface Row {
  workGroupNo: number;
  workGroup: string;
  achieved: number;
  target: number;
  percentage: number;
  status: string;
}

export default function OverallScheduleStatus({
  projectId,
}: {
  projectId: number;
}) {
  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // ✅ NEW API CALL (Dashboard Version)
      const result =
        await ReportAPI.getOverallScheduleStatusDashboard(
          -projectId, // keep negative if your WBS logic requires it
          0,          // lvl
          1,          // fv1
          0           // override
        );

      setData(result);
    } catch (error) {
      console.error("Error loading overall schedule", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const clean = status?.trim().toLowerCase();

    switch (clean) {
      case "held up":
      case "slow progress":
        return "bg-yellow-400";
      case "progress is ok":
        return "bg-green-500";
      case "not started":
      default:
        return "bg-red-500";
    }
  };

  return (
    <div className="bg-gray-100 rounded shadow overflow-hidden">

      {/* HEADER */}
      {/*<div className="bg-[#2F4FA2] text-white font-semibold px-5 py-3 text-sm">*/}
        <div className="bg-[#1788ac] text-white font-semibold px-5 py-3 text-sm">
        STATUS AS PER OVERALL SCHEDULE
      </div>

      {loading ? (
        <div className="bg-white p-4">Loading...</div>
      ) : (
        <div className="bg-white">
          {data.length === 0 ? (
            <div className="p-4 text-gray-500 text-sm">
              No Data Available
            </div>
          ) : (
            data.map((item) => {
              const percent = Math.min(
                Math.max(item.percentage ?? 0, 0),
                100
              );

              const status = item.status?.toLowerCase();

              const showSecondBar =
                status === "slow progress" ||
                status === "progress is ok";

              const secondBarColor =
                status === "slow progress"
                  ? "#F59E0B"
                  : status === "progress is ok"
                  ? "#28A745"
                  : "";

              return (
                <div
                  key={item.workGroupNo}
                  className="border-b px-5 py-4 last:border-b-0"
                >
                  {/* TITLE */}
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-gray-800 font-medium text-[14px]">
                      {item.workGroup}
                    </div>

                    <div className="text-gray-700 text-sm">
                      {(item.achieved * 100).toFixed(2)} of{" "}
                      {(item.target * 100).toFixed(2)}%
                    </div>
                  </div>

                  {/* First Blue Bar */}
                  <div
                    className="w-full h-[6px] mb-2"
                    style={{ backgroundColor: "#4A7BD0" }}
                  />

                  {/* Second Progress Bar */}
                  {showSecondBar && (
                    <div
                      className="h-[6px] mb-3"
                      style={{
                        width: `${percent}%`,
                        backgroundColor: secondBarColor,
                      }}
                    />
                  )}

                  {/* STATUS */}
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded-full ${getStatusColor(
                        item.status
                      )}`}
                    />
                    <div className="text-gray-700 text-sm">
                      {item.status}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}